import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// 418/421: den party jako časová osa — jediný editor party v deníku (varianta A).
// Řádek = člověk, pruhy = jeho směny ten den: terakota = přihlásil se sám na této zakázce, obrys = parta
// (tahá se: konec = čas a hodiny, střed = posun se stejnými hodinami, šrafy = přestávka), šedá = jiná zakázka,
// šrafy = běží / denní výkaz bez hodin, čárkovaně = volný čas (klik = do party), tečkovaně = jen v deníku.
export const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ""); return m ? +m[1] * 60 + +m[2] : null; };
export const hm = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
export const hh = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
export const num = (v) => Number(String(v ?? "").replace(",", "."));
export const fmtHours = (h) => `${String(Math.round(Number(h || 0) * 100) / 100).replace(".", ",")} h`;
const STEP = 15;
const STRIPES = { backgroundImage: "repeating-linear-gradient(135deg, hsl(var(--muted-foreground) / .38) 0 7px, transparent 7px 13px)" };
const BRK_STRIPES = { backgroundImage: "repeating-linear-gradient(135deg, currentColor 0 2px, transparent 2px 6px)" };
const norm = (x) => String(x || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const KINDS = [["sub", "Řemeslník / subdodávka", "Řemeslník"], ["kd", "Kontrolní den", "KD"], ["tdi", "Technický dozor", "TDI"],
  ["bozp", "Kontrola / BOZP", "BOZP"], ["jine", "Jiné", "Jiné"]];
const kindOf = (k) => KINDS.find((x) => x[0] === k) || KINDS[4];

// přestávka řádku party: délka (auto podle firmy nebo ručně) a začátek (ručně, jinak firemní break_start)
export const autoBrk = (rule, hours) => (hours > (rule?.min_hours ?? 6) ? (rule?.minutes ?? 30) : 0);
export function rowSpan(r, rule) {
  const a = toMin(r.start);
  const h = num(r.hours);
  if (a == null || !(h > 0)) return null;
  const len = r.brk == null ? autoBrk(rule, h) : r.brk;
  const b = a + Math.round(h * 60) + len;
  const at0 = toMin(r.brkAt) ?? toMin(rule?.start) ?? 12 * 60;
  const at = len ? Math.min(Math.max(at0, a), b - len) : null;
  return { a, b, len, at };
}
// z rozpětí od–do zpět na čisté hodiny (auto přestávka podle pravidla firmy, ruční se odečte)
export function hoursOf(a, b, brk, rule) {
  const gross = b - a;
  if (brk != null) return Math.max(0.25, (gross - brk) / 60);
  const withBrk = (gross - (rule?.minutes ?? 30)) / 60;
  return withBrk > (rule?.min_hours ?? 6) ? withBrk : Math.max(0.25, Math.min(gross / 60, rule?.min_hours ?? 6));
}

export function daySummary(t) {
  const parts = (t.day || []).filter((s) => s.kind !== "crew").map((s) => `${s.project} ${hm(toMin(s.start))}–${s.end ? hm(toMin(s.end)) : "teď"}`);
  if (t.report && !parts.length && t.report.start) parts.push(`denní výkaz od ${hm(toMin(t.report.start))}`);
  return parts.join(", ");
}
export const canAdd = (t) => !!(t.free && !t.free.over);

// kdo ve vyhledávání / štítcích: lidé z týmu, kteří ještě nejsou v partě ani se sami nepřihlásili sem
export function findList(team, rows, q, chip) {
  const inRows = new Set(rows.map((r) => r.user_id));
  const rest = (team || []).filter((t) => !inRows.has(t.id) && !(t.day || []).some((s) => s.kind === "here"));
  const cat = {
    free: rest.filter(canAdd),
    jinde: rest.filter((t) => (t.day || []).some((s) => s.kind === "other")),
    none: rest.filter((t) => !(t.day || []).length && !t.report),
  };
  const n = norm(q.trim());
  const list = n ? rest.filter((t) => norm(t.name).includes(n) || norm(t.position).includes(n))
    : chip ? (chip === "all" ? rest : cat[chip] || []) : [];
  return { rest, cat, list };
}

export function CrewTimeline({ team, rows, manual, rule, today, now, q, chip, setChip, onClear, flash, onAdd, onRowsCommit, onOpenRow,
  onShift, onOpenManual, onNewManual, mobile }) {
  const [drag, setDrag] = useState(null);       // { uid, mode, x0, w, a0, b0, at0, len, a, b, at }
  const dragRef = useRef(null);
  dragRef.current = drag;
  const inRows = new Map(rows.map((r) => [r.user_id, r]));
  const byId = new Map((team || []).map((t) => [t.id, t]));
  const nowMin = today ? toMin(now) : null;

  const { rest, cat, list } = findList(team, rows, q, chip);
  const open = !!(q.trim() || chip);
  const shown = list.slice(0, 6);
  const first = list.find(canAdd);

  const mainIds = [...new Set([...rows.map((r) => r.user_id), ...(team || []).filter((t) => (t.day || []).some((s) => s.kind === "here")).map((t) => t.id)])];
  const main = mainIds.map((id) => byId.get(id) || { id, name: inRows.get(id)?.name || "—", position: inRows.get(id)?.position || "", day: [] })
    .sort((x, y) => (inRows.get(y.id)?.addedAt || 0) - (inRows.get(x.id)?.addedAt || 0) || (x.name || "").localeCompare(y.name || "", "cs"));

  // okno osy: 6–18 h, roztáhne se, když někdo začal dřív nebo skončil později
  const spans = [];
  [...main, ...shown].forEach((t) => (t.day || []).forEach((s) => { spans.push(toMin(s.start)); spans.push(s.end ? toMin(s.end) : nowMin ?? toMin(s.start)); }));
  rows.forEach((r) => { const sp = rowSpan(r, rule); if (sp) { spans.push(sp.a); spans.push(sp.b); } });
  (manual || []).forEach((m) => { if (m.start && m.end) { spans.push(toMin(m.start)); spans.push(toMin(m.end)); } });
  const vals = spans.filter((x) => x != null);
  const lo = Math.min(6 * 60, ...vals.map((x) => Math.floor(x / 60) * 60));
  const hi = Math.max(18 * 60, ...vals.map((x) => Math.ceil(x / 60) * 60));
  const span = hi - lo;
  const tickStep = span > 14 * 60 ? 180 : 120;
  const ticks = [];
  for (let x = Math.ceil(lo / tickStep) * tickStep; x <= hi; x += tickStep) ticks.push(x);
  const pos = (x) => `${((Math.min(Math.max(x, lo), hi) - lo) / span) * 100}%`;
  const wid = (a, b) => `${Math.max(((Math.min(b, hi) - Math.max(a, lo)) / span) * 100, 1.2)}%`;

  // ---------- tahání ----------
  const startDrag = (e, uid, mode) => {
    const r = inRows.get(uid); const sp = r && rowSpan(r, rule);
    if (!sp) return;
    const track = e.currentTarget.closest("[data-track]");
    e.preventDefault(); e.stopPropagation();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* jsdom */ }
    setDrag({ uid, mode, x0: e.clientX, w: track?.getBoundingClientRect().width || 600, a0: sp.a, b0: sp.b, at0: sp.at, len: sp.len,
      a: sp.a, b: sp.b, at: sp.at, moved: 0 });
  };
  useEffect(() => {
    if (!drag) return undefined;
    const others = (byId.get(drag.uid)?.day || []).filter((s) => s.kind !== "crew").map((s) => ({ a: toMin(s.start), b: s.end ? toMin(s.end) : nowMin ?? hi }));
    const move = (e) => {
      const d = dragRef.current; if (!d) return;
      const dx = e.clientX - d.x0;
      const dm = Math.round((dx / d.w) * span / STEP) * STEP;
      let { a0: a, b0: b } = d, at = d.at0;
      if (d.mode === "k") at = Math.min(Math.max(d.at0 + dm, d.a0), d.b0 - d.len);
      else if (d.mode === "l") a = Math.min(d.a0 + dm, d.b0 - 30);
      else if (d.mode === "r") b = Math.max(d.b0 + dm, d.a0 + 30);
      else { a = d.a0 + dm; b = d.b0 + dm; if (at != null) at = d.at0 + dm; }
      if (a < lo) { if (d.mode === "m") { b += lo - a; if (at != null) at += lo - a; } a = lo; }
      if (b > hi) { if (d.mode === "m") { a -= b - hi; if (at != null) at -= b - hi; } b = hi; }
      for (const o of others) {                      // nesmí přes směnu na jiné zakázce
        if (d.mode === "k" || !(a < o.b && b > o.a)) continue;
        if (d.mode === "l") a = o.b; else if (d.mode === "r") b = o.a;
        else if (dm > 0) { const L = b - a; b = o.a; a = b - L; } else { const L = b - a; a = o.b; b = a + L; }
      }
      setDrag({ ...d, a, b, at, moved: Math.max(d.moved, Math.abs(dx)) });
    };
    const up = () => {
      const d = dragRef.current; setDrag(null);
      if (!d) return;
      if (d.moved < 4) { onOpenRow?.(d.uid); return; }          // klik = přesné časy a přestávka v panelu
      const r = inRows.get(d.uid);
      if (d.mode === "k") { onRowsCommit?.(d.uid, { brkAt: hh(d.at) }, `Přestávka · ${r.name} ${hm(d.at)}–${hm(d.at + d.len)}`); return; }
      if (d.a === d.a0 && d.b === d.b0) return;
      const hours = d.mode === "m" ? num(r.hours) : hoursOf(d.a, d.b, r.brk, rule);
      const patch = { start: hh(d.a), hours: Math.round(hours * 4) / 4, ...(d.mode === "m" && d.at != null && r.brkAt ? { brkAt: hh(d.at) } : {}) };
      onRowsCommit?.(d.uid, patch, `Uloženo · ${r.name} ${hm(d.a)}–${hm(d.b)}`);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
    window.addEventListener("pointercancel", up, { once: true });
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); };
  }, [drag?.uid, drag?.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // klávesnice na pruhu party: ←/→ posun o 15 min, Shift + ←/→ konec, Enter / mezerník panel
  const onBarKey = (e, uid) => {
    const r = inRows.get(uid); const sp = r && rowSpan(r, rule);
    if (!sp) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenRow?.(uid); return; }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const d = e.key === "ArrowRight" ? STEP : -STEP;
    if (e.shiftKey) { const b = Math.max(sp.a + 30, sp.b + d); onRowsCommit?.(uid, { hours: Math.round(hoursOf(sp.a, b, r.brk, rule) * 4) / 4 }, `Uloženo · ${r.name} ${hm(sp.a)}–${hm(b)}`); return; }
    onRowsCommit?.(uid, { start: hh(sp.a + d), ...(r.brkAt ? { brkAt: hh(toMin(r.brkAt) + d) } : {}) }, `Uloženo · ${r.name} ${hm(sp.a + d)}–${hm(sp.b + d)}`);
  };

  const clickSeg = (t, s) => {
    if (s.editable) return onShift?.(s.id);
    toast(`${s.project}: směnu opraví stavbyvedoucí té zakázky nebo majitel.`);
  };

  const tickLines = ticks.map((x) => <i key={x} aria-hidden className="absolute top-0 bottom-0 w-px bg-border" style={{ left: pos(x) }} />);
  const nowLine = nowMin != null && nowMin > lo && nowMin < hi ? <i aria-hidden className="absolute -top-1 -bottom-1 w-0.5 bg-destructive" style={{ left: pos(nowMin) }} /> : null;

  const segBars = (t) => (t.day || []).filter((s) => s.kind !== "crew").map((s) => {
    const a = toMin(s.start), b = s.end ? toMin(s.end) : nowMin ?? Math.min(a + 60, hi);
    const running = !s.end;
    const here = s.kind === "here";
    const label = `${here ? "" : `${s.project} `}${hm(a)}–${s.end ? hm(b) : "teď"}`;
    const len = here ? (s.break_minutes || 0) : 0;
    const at = len ? Math.min(Math.max(toMin(s.break_at) ?? toMin(rule?.start) ?? 720, a), b - len) : null;
    return (
      <button key={s.id} type="button" onClick={() => clickSeg(t, s)} title={label} aria-label={`${t.name}: ${label}`}
        className={`absolute top-1 bottom-1 rounded-[7px] px-1.5 text-left text-[11px] md:text-[11.5px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${here ? "bg-brand text-white" : running ? "text-foreground border border-border" : "bg-muted-foreground/30 text-foreground"}`}
        style={{ left: pos(a), width: wid(a, b), ...(running ? STRIPES : null) }} data-testid={`crew-tl-bar-${here ? "here" : "other"}`}>
        {label}
        {len > 0 && b > a && <i aria-hidden className="absolute top-[3px] bottom-[3px] rounded-[3px] opacity-50" style={{ left: `${((at - a) / (b - a)) * 100}%`, width: `${(len / (b - a)) * 100}%`, ...BRK_STRIPES }} />}
      </button>
    );
  });
  const crewBar = (t) => {
    const r = inRows.get(t.id); const sp0 = r && rowSpan(r, rule);
    if (!sp0) return null;
    const d = drag && drag.uid === t.id ? drag : null;
    const sp = d ? { a: d.a, b: d.b, len: d.len, at: d.mode === "k" || d.mode === "m" ? d.at : sp0.len ? Math.min(Math.max(sp0.at, d.a), d.b - sp0.len) : null } : sp0;
    const hrs = d ? (d.mode === "m" ? num(r.hours) : hoursOf(sp.a, sp.b, r.brk, rule)) : num(r.hours);
    const label = `${hm(sp.a)}–${hm(sp.b)}`;
    return (
      <div key={`crew-${t.id}`} role="slider" tabIndex={0} aria-label={`${t.name}: parta ${label}, ${fmtHours(hrs)}. Šipky posunou, Shift + šipky změní konec, Enter otevře úpravu`}
        aria-valuemin={lo} aria-valuemax={hi} aria-valuenow={sp.a} onKeyDown={(e) => onBarKey(e, t.id)}
        onPointerDown={(e) => startDrag(e, t.id, "m")}
        className={`absolute top-1 bottom-1 rounded-[7px] bg-brand/10 border-[1.5px] border-brand text-brand text-[11px] md:text-[11.5px] font-semibold tabular-nums select-none touch-none flex items-center ${d ? "cursor-grabbing shadow-lg z-[3]" : "cursor-grab"} focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand`}
        style={{ left: pos(sp.a), width: wid(sp.a, sp.b) }} data-testid="crew-tl-bar-crew">
        <span onPointerDown={(e) => startDrag(e, t.id, "l")} aria-hidden className="absolute left-0 top-0 bottom-0 w-3 md:w-2.5 cursor-ew-resize grid place-items-center" data-testid="crew-tl-handle-l"><i className="w-[3px] h-3 rounded bg-current opacity-60" /></span>
        <span className="pointer-events-none px-3 md:px-2.5 truncate">{label}</span>
        {sp.len > 0 && sp.at != null && (
          <span onPointerDown={(e) => startDrag(e, t.id, "k")} title={`Přestávka ${hm(sp.at)}–${hm(sp.at + sp.len)} — posuň ji, klikni pro délku`}
            className={`absolute top-[3px] bottom-[3px] rounded-[3px] cursor-grab ${r.brk == null ? "opacity-40" : "opacity-60"} hover:opacity-80`}
            style={{ left: `${((sp.at - sp.a) / (sp.b - sp.a)) * 100}%`, width: `${(sp.len / (sp.b - sp.a)) * 100}%`, minWidth: 8, ...BRK_STRIPES }} data-testid="crew-tl-break" />
        )}
        <span onPointerDown={(e) => startDrag(e, t.id, "r")} aria-hidden className="absolute right-0 top-0 bottom-0 w-3 md:w-2.5 cursor-ew-resize grid place-items-center" data-testid="crew-tl-handle-r"><i className="w-[3px] h-3 rounded bg-current opacity-60" /></span>
        {d && d.moved >= 4 && (
          <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground text-background px-2 py-0.5 text-[11.5px] font-semibold pointer-events-none z-10">
            {d.mode === "k" ? `Přestávka ${hm(sp.at)}–${hm(sp.at + sp.len)}` : `${label} · ${fmtHours(Math.round(hrs * 4) / 4)}${d.mode === "m" ? " · posun" : ""}`}
          </span>
        )}
      </div>
    );
  };
  const reportBar = (t) => {
    if (!t.report || (t.day || []).some((s) => s.kind !== "crew")) return null;
    const a = toMin(t.report.start); if (a == null) return null;
    const b = t.report.end ? toMin(t.report.end) : nowMin ?? Math.min(a + 60, hi);
    return (
      <button key="rep" type="button" onClick={() => toast(t.report.status === "open" ? `${t.name} píše denní výkaz. Hodiny po zakázkách si rozdělí sám při odeslání.` : `${t.name}: ${t.why || "denní výkaz bez hodin"}. Ať ho odešle v Docházce.`)}
        className="absolute top-1 bottom-1 rounded-[7px] px-1.5 text-left text-[11px] font-semibold whitespace-nowrap overflow-hidden border border-border text-foreground"
        style={{ left: pos(a), width: wid(a, b), ...STRIPES }} data-testid="crew-tl-bar-report">{t.report.status === "open" ? `denní výkaz od ${hm(a)}` : "neodeslaný výkaz"}</button>
    );
  };
  const ghostBar = (t) => {
    if (!canAdd(t)) return null;
    const a = toMin(t.free.start), b = a + Math.round(t.free.hours * 60) + autoBrk(rule, t.free.hours);
    return (
      <button type="button" onClick={() => onAdd?.(t.id)} aria-label={`Přidat ${t.name} ${hm(a)}–${hm(b)}`}
        className="absolute top-1 bottom-1 rounded-[7px] border-[1.5px] border-dashed border-brand/60 text-brand hover:bg-brand/10 text-[11px] md:text-[11.5px] font-bold whitespace-nowrap overflow-hidden tabular-nums flex items-center justify-center"
        style={{ left: pos(a), width: wid(a, b) }} data-testid={`crew-tl-free-${t.id}`}>
        + <span className="hidden sm:inline ml-0.5">{hm(a)}–{hm(b)}</span>
      </button>
    );
  };

  const hoursHere = (t) => (t.day || []).filter((s) => s.kind === "here").reduce((s, x) => s + (x.hours || 0), 0) + (inRows.get(t.id) ? num(inRows.get(t.id).hours) || 0 : 0);
  const hoursCell = (t) => {
    const here = hoursHere(t);
    const oth = (t.day || []).filter((s) => s.kind === "other").reduce((s, x) => s + (x.hours || 0), 0);
    const tot = here + oth, target = t.target || 8;
    return (
      <span className="text-right leading-tight tabular-nums">
        <b className="text-sm">{fmtHours(here)}</b>
        {oth > 0 && <span className={`block text-[11px] font-semibold whitespace-nowrap ${tot > target + 0.01 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} title="Za celý den na všech zakázkách" data-testid={`crew-tl-total-${t.id}`}>
          celkem {fmtHours(tot)}{tot > target + 0.01 ? ` · +${fmtHours(tot - target)}` : ""}</span>}
      </span>
    );
  };
  const subOf = (t, isMain) => {
    if (isMain) return (t.day || []).some((s) => s.kind === "here") && !inRows.get(t.id) ? "přihlásil se sám" : t.position || "";
    if (t.why) return t.why;
    if (t.free?.whole_day) return "ten den bez docházky";
    if (t.free?.over) return `celý den jinde · ${fmtHours(t.free.worked)}`;
    return `${daySummary(t)}${t.free ? ` · volno od ${hm(toMin(t.free.start))}` : ""}`;
  };
  const row = (t, isMain) => (
    <div key={t.id} className={`grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)_96px] md:items-center gap-x-3 gap-y-1 rounded-[10px] transition-colors motion-reduce:transition-none ${isMain && flash === t.id ? "bg-brand/10" : ""}`} data-testid={`crew-tl-row-${t.id}`}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <div className="font-semibold text-[13.5px] truncate">{t.name}</div>
          <div className={`text-[12px] truncate ${t.why && !isMain ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>{subOf(t, isMain)}</div>
        </div>
        {isMain && mobile && hoursCell(t)}
      </div>
      <div className="relative h-9 rounded-[10px] bg-secondary/60 touch-pan-y" data-track={t.id} data-testid={`crew-tl-track-${t.id}`}>
        {tickLines}{segBars(t)}{isMain ? crewBar(t) : null}{reportBar(t)}{!isMain && ghostBar(t)}{nowLine}
      </div>
      {!mobile && <div className="hidden md:block text-right">{isMain ? hoursCell(t) : null}</div>}
    </div>
  );
  const manRow = (m, i) => {
    const a = toMin(m.start), b = toMin(m.end), timed = a != null && b != null;
    const k = kindOf(m.kind);
    const hrs = timed ? (b - a) / 60 : num(m.hours) || 0;
    const cell = (
      <span className="text-right leading-tight tabular-nums"><b className="text-sm">{timed || hrs ? fmtHours(hrs) : "—"}</b>
        <span className="block text-[11px] text-muted-foreground whitespace-nowrap">bez docházky</span></span>
    );
    return (
      <div key={`m-${i}`} className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)_96px] md:items-center gap-x-3 gap-y-1" data-testid={`crew-tl-manual-${i}`}>
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="min-w-0">
            <div className="font-semibold text-[13.5px] truncate">{m.name}</div>
            <div className="text-[12px] text-muted-foreground truncate" title={`${k[1]}${m.role ? ` · ${m.role}` : ""}`}>
              <span className="inline-block mr-1 px-1 rounded border border-border text-[10px] font-bold uppercase tracking-wide align-[1px]">{k[2]}</span>{m.role}</div>
          </div>
          {mobile && cell}
        </div>
        <div className="relative h-9 rounded-[10px] bg-secondary/60" data-testid={`crew-tl-mtrack-${i}`}>
          {tickLines}
          <button type="button" onClick={() => onOpenManual?.(i)} className="absolute top-1 bottom-1 rounded-[7px] border-[1.5px] border-dotted border-muted-foreground/60 text-muted-foreground text-[11px] md:text-[11.5px] font-semibold whitespace-nowrap overflow-hidden tabular-nums px-2 text-left"
            style={timed ? { left: pos(a), width: wid(a, b) } : { left: pos(7 * 60), width: wid(7 * 60, 17 * 60) }}>
            {timed ? `${hm(a)}–${hm(b)}` : "bez času — klikni a doplň"}
          </button>
        </div>
        {!mobile && <div className="hidden md:block text-right">{cell}</div>}
      </div>
    );
  };

  const chipBtn = (k, label, n) => (
    <button key={k} type="button" onClick={() => setChip?.(chip === k && !q.trim() ? null : k)} aria-pressed={!q.trim() && chip === k}
      className={`h-8 px-3 rounded-full border text-[12.5px] font-semibold whitespace-nowrap ${!q.trim() && chip === k ? "border-brand bg-brand/10 text-brand" : "border-border bg-card hover:bg-secondary"}`} data-testid={`crew-chip-${k}`}>
      {label}<span className="ml-1 opacity-60 tabular-nums">{n}</span>
    </button>
  );
  const sep = (text, testid) => <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground border-t border-border pt-2 mt-1" data-testid={testid}>{text}</div>;

  return (
    <div className="rounded-[14px] border border-border p-3 md:p-4 space-y-2" data-testid="crew-timeline">
      <div className="hidden md:grid grid-cols-[200px_minmax(0,1fr)_96px] gap-x-3">
        <span /><div className="relative h-4 text-[11px] text-muted-foreground tabular-nums">
          {ticks.map((x) => <span key={x} className="absolute -translate-x-1/2" style={{ left: pos(x) }}>{hm(x)}</span>)}</div><span />
      </div>
      <div className="md:hidden relative h-4 text-[10.5px] text-muted-foreground tabular-nums">
        {ticks.map((x) => <span key={x} className="absolute -translate-x-1/2" style={{ left: pos(x) }}>{Math.floor(x / 60)}</span>)}</div>

      {/* Ostatní z týmu / hledání — NAD partou, rozbalí se dolů (Skrýt zůstane nahoře) */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5" data-testid="crew-find-bar">
        <b className="text-[13px] mr-0.5">{q.trim() ? `Hledám „${q.trim()}“` : "Ostatní z týmu"}</b>
        {!q.trim() && <>{chipBtn("free", "Mají volno", cat.free.length)}{chipBtn("jinde", "Byli jinde", cat.jinde.length)}{chipBtn("none", "Bez docházky", cat.none.length)}{chipBtn("all", "Všichni", rest.length)}</>}
        {open && <button type="button" onClick={onClear} className="md:ml-auto h-8 px-2 text-[12.5px] font-semibold text-brand" data-testid="crew-find-hide">Skrýt ▴</button>}
      </div>
      {open && (
        <div className="space-y-2.5 md:space-y-1.5" data-testid="crew-find-results">
          {shown.map((t) => row(t, false))}
          {q.trim() && !list.length && <p className="text-[12.5px] text-muted-foreground">V týmu zakázky nikdo takový není.</p>}
          {list.length > shown.length && <p className="text-[12.5px] text-muted-foreground">a dalších {list.length - shown.length} — upřesni hledání nahoře</p>}
          {q.trim() && first && <p className="text-[12.5px] text-muted-foreground">Enter přidá {first.name} na volný čas od {hm(toMin(first.free.start))}</p>}
          {q.trim() && (
            <button type="button" onClick={() => onNewManual?.(q.trim())} className="w-full md:w-auto h-10 px-3 rounded-[10px] border border-border text-sm font-semibold hover:bg-secondary text-left" data-testid="crew-find-manual">
              + Přidat „{q.trim()}“ mimo tým — elektrikář, kontrolní den, kontrola…</button>
          )}
        </div>
      )}

      {sep(`V partě · ${main.length}`, "crew-sep-main")}
      <div className="space-y-2.5 md:space-y-1.5">
        {main.map((t) => row(t, true))}
        {!main.length && <p className="text-[12.5px] text-muted-foreground" data-testid="crew-empty">Na zakázce zatím nikdo. Najdi lidi hledáním nahoře, nebo klikni na štítek Mají volno.</p>}
      </div>
      {(manual || []).length > 0 && (<>
        {sep(`Jen v deníku · bez docházky · ${manual.length}`, "crew-sep-manual")}
        <div className="space-y-2.5 md:space-y-1.5">{manual.map(manRow)}</div>
      </>)}

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-muted-foreground pt-1">
        <span className="inline-flex items-center gap-1.5"><i className="w-3.5 h-2.5 rounded-[3px] bg-brand" />přihlásil se sám</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3.5 h-2.5 rounded-[3px] bg-brand/10 border-[1.5px] border-brand" />parta — tahej za konec nebo střed</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3.5 h-2.5 rounded-[3px] text-brand" style={BRK_STRIPES} />přestávka</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3.5 h-2.5 rounded-[3px] bg-muted-foreground/30" />jiná zakázka</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3.5 h-2.5 rounded-[3px] border-[1.5px] border-dashed border-brand/60" />volný čas</span>
      </div>
    </div>
  );
}
